import React, { useState, useMemo, useRef, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import { Copy, Check, Search, Shapes, RefreshCw, GripVertical } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';

const IconBrowserPage = () => {
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedIcon, setCopiedIcon] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { notify } = useNotifications();

  useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get all icon names (filter out non-icon exports)
  const allIconNames = useMemo(() => {
    return Object.keys(LucideIcons).filter(name => {
      return /^[A-Z][a-zA-Z0-9]*$/.test(name) &&
             !name.endsWith('Icon') &&
             name !== 'default' &&
             name !== 'createLucideIcon';
    }).sort();
  }, []);

  // Fuzzy filter based on search term
  const filteredIcons = useMemo(() => {
    if (!searchTerm.trim()) return allIconNames;
    const term = searchTerm.toLowerCase();
    return allIconNames.filter(name =>
      name.toLowerCase().includes(term)
    );
  }, [allIconNames, searchTerm]);

  // Copy icon name to clipboard
  const handleCopyIcon = async (iconName) => {
    try {
      await navigator.clipboard.writeText(iconName);
      setCopiedIcon(iconName);
      notify.success(`Copied ${iconName}`);
      setTimeout(() => setCopiedIcon(null), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = iconName;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedIcon(iconName);
      notify.success(`Copied ${iconName}`);
      setTimeout(() => setCopiedIcon(null), 2000);
    }
  };

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <Shapes size={20} />
          <h1>Icon Browser</h1>
          <span className="stui-module-header-meta">{filteredIcons.length} of {allIconNames.length} icons</span>
        </div>
        <div className="stui-module-header-actions">
          <div className="stui-search-wrapper">
            <Search size={14} className="stui-search-icon" />
            <input
              className="stui-search-input"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') setSearchTerm(inputValue); }}
              placeholder="Search icons…  ↵"
            />
          </div>
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); setInputValue(''); setSearchTerm(''); }}><RefreshCw size={16} /><span>Reset</span></button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="stui-tab-body">
        <div className="stui-icon-grid">
          {filteredIcons.map(iconName => {
            const IconComponent = LucideIcons[iconName];
            const isCopied = copiedIcon === iconName;
            return (
              <button
                key={iconName}
                className={`stui-icon-card${isCopied ? ' copied' : ''}`}
                onClick={() => handleCopyIcon(iconName)}
                title={`Click to copy: ${iconName}`}
              >
                <div className="stui-icon-preview">
                  <IconComponent size={24} />
                </div>
                <div className="stui-icon-name">{iconName}</div>
                <div className="stui-icon-copy-indicator">
                  {isCopied ? <Check size={12} /> : <Copy size={12} />}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default IconBrowserPage;
